import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  PrintingService,
  ResponsePrintingServiceProps,
  PrintingServiceList,
} from './PrintingService';
import { UserAuth } from '../users/User';
import { ResponseUsersProps } from '../users/User';
import { FoodCategory } from '../foodCategories/FoodCategory';
import { GetCategoriesProps } from '../../store/actions/foodCategories/FoodCategoriesTypes';
import { PrinterServerConfiguration, Setting } from '../settings/Setting';
import { GetSettings } from '../../store/actions/settings/SettingTypes';
import { PrinterType } from '../../utils/enums';
import { ResponseCashRegister } from '../cashRegister/CashRegister';

export interface LocalPrinter {
  id: string;
  name: string;
  type: string;
  connectionType: string;
  address: string;
  status: string;
  configuration: any;
  isDefault: boolean;
  lastSeen: number;
  capabilities: string[];
}

export interface PrintingServiceMainProps {
  printingServices: ResponsePrintingServiceProps;
  listPrintingService: (
    numberPage: number,
    limit: number,
    accountId: string,
    search: string,
    filter: string
  ) => Promise<void>;
  deletePrintingService: (
    data: {
      id: string;
      accountId: string;
    },
    callbackSuccess?: Function,
    callbackError?: Function
  ) => Promise<void>;
  user: UserAuth;
}

export interface PrintingServiceCreateProps {
  createPrintingService: (
    data: PrintingServiceFormValues,
    callbackSuccess?: Function,
    callbackError?: Function
  ) => Promise<void>;
  user: UserAuth;
  listUsers: (
    currentPage: number,
    limit: number,
    search: string,
    callbackSuccess?: Function,
    callbackError?: Function
  ) => Promise<void>;
  listPrintingService: (accountId: string) => Promise<void>;
  users: ResponseUsersProps;
  printingServiceList: PrintingServiceList[];
  categories: FoodCategory[];
  getCategories: ({ ...args }: GetCategoriesProps) => Promise<void>;
  listCashRegister: (
    numberPage: number,
    limit: number,
    accountId: string,
    search: string | null,
    filter: string | null
  ) => Promise<void>;
  cashRegisters: ResponseCashRegister;
}

export interface PrintServerProps {
  user: UserAuth;
  settings: Setting;
  getSettings: ({
    accountId,
    callbackError,
    callbackSuccess,
  }: GetSettings) => Promise<void>;
}

export interface PrintingServiceEditProps {
  updatePrintingService: (
    data: PrintingServiceFormValues,
    callbackSuccess?: Function,
    callbackError?: Function
  ) => Promise<void>;
  user: UserAuth;
  listUsers: (
    currentPage: number,
    limit: number,
    search: string,
    callbackSuccess?: Function,
    callbackError?: Function
  ) => Promise<void>;
  listPrintingService: (accountId: string) => Promise<void>;
  users: ResponseUsersProps;
  printingServiceList: PrintingServiceList[];
  categories: FoodCategory[];
  getCategories: ({ ...args }: GetCategoriesProps) => Promise<void>;
  showPrintingService: (
    id: string,
    accountId: string,
    callbackSuccess?: Function,
    callbackError?: Function
  ) => Promise<void>;
  printingService: PrintingService;
  listCashRegister: (
    numberPage: number,
    limit: number,
    accountId: string,
    search: string | null,
    filter: string | null
  ) => Promise<void>;
  cashRegisters: ResponseCashRegister;
}

export interface PrintingServiceFormValues
  extends Omit<PrintingService, 'createdAt' | 'updatedAt' | '_id'> {
  id?: string;
  tempUserId?: string;
}

export const printingServiceInitialValues: PrintingServiceFormValues = {
  account: '',
  type: '',
  connectionType: '',
  name: '',
  description: '',
  printerId: '',
  printerData: undefined,
  foodCategoryId: [],
  userPrinter: null,
  cashRegisterIds: [],
  copies: 1,
  id: '',
  tempUserId: '',
};
export const printingServiceValidationSchema = Yup.object().shape({
  account: Yup.string().notRequired(),
  type: Yup.string().required('El tipo es requerido'),
  connectionType: Yup.string().required('El tipo de conexión es requerido'),
  name: Yup.string().required('El nombre de la impresora es requerido'),
  description: Yup.string().notRequired(),
  printerId: Yup.string().required('El ID de la impresora es requerido'),
  printerData: Yup.mixed().notRequired(),
  foodCategoryId: Yup.array().of(Yup.string()).notRequired(),
  cashRegisterIds: Yup.array()
    .of(Yup.string())
    .when('type', {
      is: PrinterType.CASH_REGISTER,
      then: (schema) =>
        schema
          .min(1, 'Debe seleccionar al menos una caja registradora')
          .required('Seleccione al menos una caja registradora'),
      otherwise: (schema) => schema.notRequired(),
    }),
  userPrinter: Yup.object()
    .shape({
      id: Yup.string().required('El ID del usuario es requerido'),
      firstName: Yup.string().required('El nombre es requerido'),
      lastName: Yup.string().required('El apellido es requerido'),
      role: Yup.string().required('El rol es requerido'),
    })
    .notRequired()
    .nullable(),
  copies: Yup.number()
    .typeError('Debe ser un número')
    .min(1, 'Debe ser al menos 1 copia')
    .max(10, 'No puede exceder 10 copias')
    .required('El número de copias es requerido'),
  id: Yup.string().notRequired(),
  tempUserId: Yup.string().notRequired(),
});

export const printingServiceInitialState: PrintingService = {
  account: '',
  type: '',
  connectionType: '',
  name: '',
  printerId: '',
  cashRegisterIds: [],
  copies: 1,
};

export interface PrintServerValues extends PrinterServerConfiguration {}

export const printServerInitialValues: PrintServerValues = {
  type: 'android',
  ip: '',
  port: '8080',
  status: 'activo',
};

export const printServerSettingsValidationSchema = Yup.object({
  type: Yup.string()
    .oneOf(['android', 'windows'], 'El tipo debe ser Android o Windows')
    .required('El tipo es requerido'),
  ip: Yup.string()
    .matches(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
      'Debe ser una dirección IP válida (ejemplo: 192.168.0.13)'
    )
    .required('La dirección IP es requerida'),
  port: Yup.string()
    .matches(/^\d+$/, 'El puerto debe contener solo números')
    .test('valid-port', 'El puerto debe estar entre 1 y 65535', (value) => {
      if (!value) return false;
      const port = parseInt(value);
      return port >= 1 && port <= 65535;
    })
    .required('El puerto es requerido'),
  status: Yup.string()
    .oneOf(['activo', 'inactivo'], 'El estado debe ser Activo o Inactivo')
    .required('El estado es requerido'),
});
